import { ColumnType, IMappingConfiguration } from '@getnuvo/importer-react'
import { DropdownOptionValidation, ValidatorAPI } from '@getnuvo/importer-react'
import { NuvoImporterQuery } from 'generated/resources/graphql'

interface UseColumnsMappingProps {
  data?: NuvoImporterQuery
  optionMappingConfiguration?: IMappingConfiguration
  isEdition: boolean
}

type DropdownOption = {
  label: string
  alternativeMatches?: string[]
  description?: string
  type: string
  value: string
  validations?: DropdownOptionValidation[] | null
}

// Backend validation structure (from GraphQL) - matches UniversalImporterValidation
type BackendValidation = {
  __typename?: 'UniversalImporterValidation'
  errorMessage: string
  operator: string
  conditions: {
    __typename?: 'UniversalImporterFilterCondition'
    field: string
    values: string[]
  }[]
}

// Convert backend validation to Nuvo format
function toNuvoDropdownValidation(validation: BackendValidation): DropdownOptionValidation {
  const validate: Record<string, Record<string, string[]>> = {}

  validation.conditions.forEach((condition) => {
    if (!validate[validation.operator]) {
      validate[validation.operator] = {}
    }
    const operatorObj = validate[validation.operator]
    if (operatorObj) {
      operatorObj[condition.field] = condition.values
    }
  })

  return {
    validate,
    errorMessage: validation.errorMessage,
  }
}

// Convert backend columnValues array to Nuvo ColumnValueAPI Record format
function convertColumnValuesToRecord(columnValues?: Array<{ column: string; values: string[] }>) {
  return (
    columnValues?.reduce<Record<string, string[]>>((acc, columnValue) => {
      if (columnValue.column && columnValue.values) {
        acc[columnValue.column] = columnValue.values
      }
      return acc
    }, {}) || undefined
  )
}

export type Column = {
  label: string
  key: string
  description?: string
  example?: string | null
  columnSize?: number | null
  validations?: ValidatorAPI[] | null
  columnType?: ColumnType | null
  dropdownOptions?: DropdownOption[] | null
  alternativeMatches?: string[]
  isMultiSelect?: boolean
  outputFormat?: string | null
  allowCustomOptions?: boolean
  numberFormat?: 'eu' | 'us'
  columnMappingConfiguration?: IMappingConfiguration
  optionMappingConfiguration?: IMappingConfiguration
  hidden?: boolean
  disabled?: boolean
}

export const useColumnsMapping = ({
  data,
  optionMappingConfiguration,
  isEdition,
}: UseColumnsMappingProps) => {
  const columns: Column[] =
    data?.universalImporter.schemaAdaptersConnection.nodes
      .filter((node) => !node.hidden || isEdition)
      .map(({ __typename, dropdownOptions, validations, customizations: _, ...nuvoField }) => {
        const mappedValidations: ValidatorAPI[] =
          validations?.map(({ __typename: _validationTypename, ...nuvoValidation }) => {
            return {
              ...nuvoValidation,
              errorMessage: nuvoValidation.errorMessage || '',
              columns: nuvoValidation.columns || undefined,
              columnValues: convertColumnValuesToRecord(nuvoValidation.columnValues || undefined),
            }
          }) || []
        const mappedDropdownOptions =
          dropdownOptions?.map(
            ({
              __typename: __dropdownOptionTypename,
              validations: dropdownValidations,
              ...option
            }) => ({
              ...option,
              validations: dropdownValidations?.map(toNuvoDropdownValidation) || null,
            })
          ) || []

        return {
          ...nuvoField,
          dropdownOptions: mappedDropdownOptions,
          validations: mappedValidations,
          isMultiSelect: nuvoField.isMultiSelect == true,
          optionMappingConfiguration,
        }
      }) || []
  return { columns }
}
